const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatPercentValue = (value) => {
  const num = toNumber(value);
  if (!Number.isFinite(num) || num === 0) return "0";
  if (num % 1 === 0) return String(num);
  return String(Number(num.toFixed(4)));
};
export const isConfectionaryRecipe = (recipeType = "") =>
  String(recipeType || "").toLowerCase().includes("confection");

export const isBeveragePsdRecipe = (recipeType = "") => {
  const t = String(recipeType || "").toLowerCase().trim();
  return (
    t === "beverage psd" ||
    t === "beverage-psd" ||
    t === "beveragepsd" ||
    t === "psd" ||
    t.includes("psd")
  );
};

export const isBeverageRecipe = (recipeType = "") => {
  const t = String(recipeType || "").toLowerCase().trim();
  return (
    t === "beverage" ||
    (t.includes("beverage") && !t.includes("psd"))
  );
};

export const isBeverageOrBeveragePsdRecipe = (recipeType = "") => {
  const t = String(recipeType || "").toLowerCase().trim();
  return (
    isBeverageRecipe(t) ||
    isBeveragePsdRecipe(t) ||
    t.includes("beverage") ||
    t.includes("psd")
  );
};


export const isBffProductIngredient = (item) => {
  if (!item) return false;
  if (item.ingredientSourceType === "bffProductCode") return true;
  if (item.ingredientSourceType === "rawMaterial") return false;
  if (item.isBFFProduct !== undefined) return Boolean(item.isBFFProduct);
  if (item.isBffProduct !== undefined) return Boolean(item.isBffProduct);
  if (item.sourceCode) return true;
  if (item.role && item.role !== "-" && item.role !== "None") return true;
  return false;
};

const toTitle = (value = "") =>
  String(value)
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const getIngredientMatchKey = (item) => {
  if (!item) return "";
  const rawSourceId =
    typeof (item.sourceId || item.ingredient) === "object" && (item.sourceId || item.ingredient) !== null
      ? (item.sourceId || item.ingredient)._id || (item.sourceId || item.ingredient).id || null
      : (item.sourceId || item.ingredient);
  const srcId = String(rawSourceId || "").trim();
  const srcType = String(item.ingredientSourceType || (item.sourceCode ? "bffProductCode" : "rawMaterial")).trim().toLowerCase();
  const roleRaw = String(item.role || "").trim().toLowerCase();
  const role = roleRaw === "-" || roleRaw === "none" ? "" : roleRaw;
  const procRaw = String(item.process || "").trim().toUpperCase();
  const process = procRaw === "-" || procRaw === "NONE" ? "" : procRaw;
  const name = String(item.name || item.ingredientName || item.sourceName || "").trim().toLowerCase();

  if (srcId) {
    return `${srcType}:${srcId}:${role}:${process}`;
  }
  return `name:${name}:${role}:${process}`;
};

export const isIngredientMatch = (a, b) => {
  if (!a || !b) return false;
  // 1. Direct _id match
  if (a._id && b._id && String(a._id) === String(b._id)) {
    return true;
  }

  // 2. Source ID match
  const rawSourceIdA =
    typeof (a.sourceId || a.ingredient) === "object" && (a.sourceId || a.ingredient) !== null
      ? (a.sourceId || a.ingredient)._id || (a.sourceId || a.ingredient).id || null
      : (a.sourceId || a.ingredient);
  const rawSourceIdB =
    typeof (b.sourceId || b.ingredient) === "object" && (b.sourceId || b.ingredient) !== null
      ? (b.sourceId || b.ingredient)._id || (b.sourceId || b.ingredient).id || null
      : (b.sourceId || b.ingredient);

  const srcIdA = String(rawSourceIdA || "").trim();
  const srcIdB = String(rawSourceIdB || "").trim();

  if (srcIdA && srcIdB && srcIdA === srcIdB) {
    const roleA = String(a.role || "").trim().toLowerCase().replace(/^[-_]$/, "");
    const roleB = String(b.role || "").trim().toLowerCase().replace(/^[-_]$/, "");
    const procA = String(a.process || "").trim().toUpperCase().replace(/^[-_]$/, "");
    const procB = String(b.process || "").trim().toUpperCase().replace(/^[-_]$/, "");

    if (roleA === roleB && procA === procB) {
      return true;
    }
    if (!roleA || !roleB || roleA === roleB) {
      return true;
    }
  }

  // 3. Match Key
  const keyA = getIngredientMatchKey(a);
  const keyB = getIngredientMatchKey(b);
  if (keyA && keyB && keyA === keyB) {
    return true;
  }

  // 4. Name match fallback
  const nameA = String(a.name || a.ingredientName || a.sourceName || a.sourceCode || "").trim().toLowerCase();
  const nameB = String(b.name || b.ingredientName || b.sourceName || b.sourceCode || "").trim().toLowerCase();
  if (nameA && nameB && nameA !== "-" && nameB !== "-") {
    if (nameA === nameB) return true;
    if (nameA.includes(nameB) || nameB.includes(nameA)) return true;
  }

  return false;
};

const buildIngredientName = (item) => {
  if (item?.sourceName && item?.sourceCode) return `${item.sourceCode} - ${item.sourceName}`;
  if (item?.sourceName) return item.sourceName;
  if (item?.name) return item.name;
  if (item?.ingredientName) return item.ingredientName;
  return "";
};

export function buildIngredientsDisplayData(recipeData, overrides = {}) {
  const ingredients = Array.isArray(recipeData?.ingredients) ? recipeData.ingredients : [];
  const isConfectionary = isConfectionaryRecipe(recipeData?.recipeType);

  const outputYield =
    overrides.yield !== undefined
      ? toNumber(overrides.yield)
      : overrides.outputYield !== undefined
        ? toNumber(overrides.outputYield)
        : toNumber(recipeData?.yield ?? recipeData?.outputYield);
  const outputServingSize =
    overrides.servingSize !== undefined
      ? toNumber(overrides.servingSize)
      : overrides.outputServingSize !== undefined
        ? toNumber(overrides.outputServingSize)
        : toNumber(recipeData?.servingSize ?? recipeData?.outputServingSize);

  const ingredientRows = ingredients.map((item, index) => {
    const quantity = toNumber(item?.quantity);
    const bffRate = toNumber(item?.bffRateAtCreation);
    const clientRate = toNumber(item?.clientRateAtCreation);

    return {
      ...item,
      sl: index + 1,
      originalIndex: index,
      role: toTitle(item?.role || "-"),
      type: toTitle(item?.type || "-"),
      process: item?.process || "",
      typeKey: String(item?.type || "").toLowerCase(),
      name: buildIngredientName(item),
      quantity,
      bffRate,
      clientRate,
      bffCost: (bffRate * quantity) / 1000,
      clientCost: (clientRate * quantity) / 1000,
      isSeparator: false,
      solidLiquid: null,
    };
  });

  const liquidRows = ingredientRows.filter((item) => item.typeKey === "liquid");
  const solidRows = ingredientRows.filter((item) => item.typeKey === "solid");
  const others = ingredientRows.filter((item) => !["liquid", "solid"].includes(item.typeKey));

  const totalQuantity = ingredientRows.reduce((sum, item) => sum + item.quantity, 0);
  const totalBFFCost = ingredientRows.reduce((sum, item) => sum + item.bffCost, 0);
  const totalClientCost = ingredientRows.reduce((sum, item) => sum + item.clientCost, 0);

  const liquidQuantity = liquidRows.reduce((sum, item) => sum + item.quantity, 0);
  const solidQuantity = solidRows.reduce((sum, item) => sum + item.quantity, 0);

  const solidPercent = totalQuantity > 0 ? (solidQuantity / totalQuantity) * 100 : 0;
  const liquidPercent = totalQuantity > 0 ? (liquidQuantity / totalQuantity) * 100 : 0;
  const totalSolidLiquid = solidPercent + liquidPercent;

  const withSolidLiquid = (rows, segmentPercent) =>
    rows.map((item, index) => ({
      ...item,
      solidLiquid: index === 0 ? formatPercentValue(segmentPercent) : null,
    }));

  let baseRows = [];

  if (isConfectionary) {
    // Confectionery: Group by step A-E and order according to rules:
    // Step A, C, E: Other Ingredients on top, BFF Products in bottom
    // Step B, D: BFF Products on top, Other Ingredients in bottom
    const processSteps = ["A", "B", "C", "D", "E"];
    const stepGroups = new Map();
    processSteps.forEach((step) => stepGroups.set(step, []));
    const otherProcessRows = [];

    ingredientRows.forEach((row) => {
      const p = String(row.process || "").toUpperCase().trim();
      if (stepGroups.has(p)) {
        stepGroups.get(p).push(row);
      } else if (p) {
        if (!stepGroups.has(p)) stepGroups.set(p, []);
        stepGroups.get(p).push(row);
      } else {
        otherProcessRows.push(row);
      }
    });

    const sortedStepKeys = Array.from(stepGroups.keys()).sort((a, b) => a.localeCompare(b));

    sortedStepKeys.forEach((stepKey) => {
      const rowsInStep = stepGroups.get(stepKey) || [];
      if (rowsInStep.length === 0) return;

      const stepBff = rowsInStep.filter(isBffProductIngredient);
      const stepOthers = rowsInStep.filter((r) => !isBffProductIngredient(r));

      // B & D: BFF Products on top, Other Ingredients in bottom
      // A, C, E (and any other steps): Other Ingredients on top, BFF Products in bottom
      if (stepKey === "B" || stepKey === "D") {
        baseRows.push(...stepBff, ...stepOthers);
      } else {
        baseRows.push(...stepOthers, ...stepBff);
      }
    });

    if (otherProcessRows.length > 0) {
      const otherBff = otherProcessRows.filter(isBffProductIngredient);
      const otherNonBff = otherProcessRows.filter((r) => !isBffProductIngredient(r));
      baseRows.push(...otherNonBff, ...otherBff);
    }

    baseRows = baseRows.map((row) => ({
      ...row,
      isSeparator: false,
      solidLiquid: null,
    }));
  } else if (isBeverageOrBeveragePsdRecipe(recipeData?.recipeType || overrides.recipeFormat)) {
    // Beverage PSD and Beverage:
    // Standard Ingredients displayed above, BFF Products section displayed below it.
    const standardIngredients = ingredientRows.filter((item) => !isBffProductIngredient(item));
    const bffProducts = ingredientRows.filter((item) => isBffProductIngredient(item));

    if (standardIngredients.length > 0 && bffProducts.length > 0) {
      standardIngredients[standardIngredients.length - 1] = {
        ...standardIngredients[standardIngredients.length - 1],
        isSeparator: true,
      };
    }

    baseRows = [...standardIngredients, ...bffProducts];
  } else {
    // Bakery
    // Liquid: Other ingredients on top, BFF Products grouped in the bottom
    // Solid: BFF Products grouped in the top, Other ingredients on bottom
    const liquidOthers = liquidRows.filter((item) => !isBffProductIngredient(item));
    const liquidBff = liquidRows.filter((item) => isBffProductIngredient(item));
    const sortedLiquidRows = [...liquidOthers, ...liquidBff];

    const solidBff = solidRows.filter((item) => isBffProductIngredient(item));
    const solidOthers = solidRows.filter((item) => !isBffProductIngredient(item));
    const sortedSolidRows = [...solidBff, ...solidOthers];

    const otherBff = others.filter((item) => isBffProductIngredient(item));
    const otherNonBff = others.filter((item) => !isBffProductIngredient(item));
    const sortedOthers = [...otherNonBff, ...otherBff];

    const normalizedLiquidRows = withSolidLiquid(sortedLiquidRows, liquidPercent);
    const normalizedSolidRows = withSolidLiquid(sortedSolidRows, solidPercent);

    if (normalizedLiquidRows.length > 0 && normalizedSolidRows.length > 0) {
      normalizedLiquidRows[normalizedLiquidRows.length - 1] = {
        ...normalizedLiquidRows[normalizedLiquidRows.length - 1],
        isSeparator: true,
      };
    }

    baseRows = [...normalizedLiquidRows, ...normalizedSolidRows, ...sortedOthers];
  }

  const isPsd = isBeveragePsdRecipe(recipeData?.recipeType || overrides.recipeFormat);

  // Diluent Name and Volume (default: "Cold Water", 100 ml)
  const diluentName =
    overrides.diluentName !== undefined
      ? overrides.diluentName
      : recipeData?.diluentName || "Cold Water";

  const diluentVolume =
    overrides.diluentVolume !== undefined
      ? toNumber(overrides.diluentVolume)
      : recipeData?.diluentVolume !== undefined && recipeData?.diluentVolume !== null
        ? toNumber(recipeData.diluentVolume)
        : 100;

  // Serving Size / Sachet Weight
  const servingSizeValue =
    overrides.servingSize !== undefined
      ? toNumber(overrides.servingSize)
      : overrides.outputServingSize !== undefined
        ? toNumber(overrides.outputServingSize)
        : recipeData?.servingSize !== undefined
          ? toNumber(recipeData.servingSize)
          : recipeData?.outputServingSize !== undefined
            ? toNumber(recipeData.outputServingSize)
            : 1.2;

  // Total Per Sachet: Quantity / Serving Size
  const totalPerSachetNum = servingSizeValue > 0 ? totalQuantity / servingSizeValue : 0;
  const totalDilutedVolume = totalPerSachetNum + diluentVolume;

  // Total Final Dosing (%): [Total Per Sachet / (Total Per Sachet + Diluent Volume)] * 100
  const totalFinalDosingNum =
    totalDilutedVolume > 0 ? (totalPerSachetNum / totalDilutedVolume) * 100 : 0;

  const orderedRows = baseRows.map((row, index) => {
    const composition = totalQuantity > 0 ? (row.quantity / totalQuantity) * 100 : 0;

    // Per Sachet (g/pcs): Quantity[i] / Serving Size
    const perSachetVal = servingSizeValue > 0 ? row.quantity / servingSizeValue : 0;

    // Final Dosing (%): [Per Sachet[i] / (Total Per Sachet + Diluent Volume)] * 100
    const finalDosingVal =
      totalDilutedVolume > 0 ? (perSachetVal / totalDilutedVolume) * 100 : 0;

    return {
      ...row,
      sl: index + 1,
      bffRate: toNumber(row.bffRate).toFixed(2),
      clientRate: toNumber(row.clientRate).toFixed(2),
      composition: formatPercentValue(composition),
      perSachet: perSachetVal % 1 === 0 ? String(perSachetVal) : perSachetVal.toFixed(2),
      finalDosing: finalDosingVal % 1 === 0 ? String(finalDosingVal) : finalDosingVal.toFixed(2),
      bffCost: toNumber(row.bffCost).toFixed(2),
      clientCost: toNumber(row.clientCost).toFixed(2),
    };
  });

  // Sum exact (unrounded) composition values to avoid rounding drift (e.g. 100.01)
  const rawTotalComposition = ingredients.reduce((sum, item) => {
    const qty = toNumber(item?.quantity);
    return sum + (totalQuantity > 0 ? (qty / totalQuantity) * 100 : 0);
  }, 0);
  const totalComposition =
    Math.abs(rawTotalComposition - 100) < 0.01 ? 100 : rawTotalComposition;

  const totalPerSachetStr =
    totalPerSachetNum > 0
      ? totalPerSachetNum % 1 === 0
        ? String(totalPerSachetNum)
        : totalPerSachetNum.toFixed(2)
      : "0.00";

  const totalFinalDosingStr =
    totalFinalDosingNum > 0
      ? totalFinalDosingNum % 1 === 0
        ? String(totalFinalDosingNum)
        : totalFinalDosingNum.toFixed(2)
      : "0.00";

  // Output Block:
  // Output (g): Total Quantity * (Yield / 100)
  // Output Pieces (pcs): Output / Serving Size
  const outputGrams = totalQuantity * (outputYield / 100);
  const outputPieces = outputServingSize > 0 ? outputGrams / outputServingSize : (servingSizeValue > 0 ? outputGrams / servingSizeValue : 0);

  // BFF Cost Calculation Block:
  // Cost Per Piece (BDT/pcs): Total BFF Cost / Packet Quantity
  // Lab Output (g): Per Piece * Packet Quantity
  // Lab Output Cost (BDT): if (Per Piece is 0 g) Display 0 else Total BFF Cost
  // Wastage (g): Total Quantity - Lab Output
  // Wastage Cost (BDT): if (Per Piece is 0 g) Total BFF Cost else Display 0
  const perPieceNum =
    overrides.perPiece !== undefined
      ? toNumber(overrides.perPiece)
      : toNumber(recipeData?.perPiece, outputServingSize > 0 ? outputServingSize : 0);

  const packetQtyNum =
    overrides.packetQuantity !== undefined
      ? toNumber(overrides.packetQuantity)
      : toNumber(recipeData?.packetQuantity, 0);

  const costPerPieceBFF = packetQtyNum > 0 ? totalBFFCost / packetQtyNum : 0;
  const labOutputGrams = perPieceNum * packetQtyNum;
  const labOutputCostBFF = perPieceNum === 0 ? 0 : totalBFFCost;
  const wastageGrams = perPieceNum > 0 && packetQtyNum > 0 ? (totalQuantity - labOutputGrams) : 0;
  const wastageCostBFF = perPieceNum === 0 ? totalBFFCost : 0;

  // Client Cost Calculation Block:
  // Cost per kg (without loss): (Total Client Cost / Total Quantity) * 1000
  // Cost per kg (with loss): (Total Client Cost / Output) * 1000
  // Cost Per Piece/Sachet: Total Client Cost / Output Pieces
  const clientCostPerKgWithoutLoss =
    totalQuantity > 0 ? (totalClientCost / totalQuantity) * 1000 : 0;
  const clientCostPerKgWithLoss =
    outputGrams > 0 ? (totalClientCost / outputGrams) * 1000 : 0;
  const clientCostPerPiece =
    outputPieces > 0 ? totalClientCost / outputPieces : 0;

  const formatCostNum = (num, fallback = "0") => {
    if (!Number.isFinite(num)) return fallback;
    return num % 1 === 0 ? String(num) : num.toFixed(2);
  };

  const formatCostCurrency = (num, fallback = "0") => {
    if (!Number.isFinite(num) || num === 0) return fallback;
    return num % 1 === 0
      ? String(num)
      : num >= 100
        ? num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : num.toFixed(2);
  };

  return {
    ingredients: orderedRows,
    diluent: {
      name: diluentName,
      volume: diluentVolume,
    },
    totals: {
      quantity: totalQuantity % 1 === 0 ? String(totalQuantity) : totalQuantity.toFixed(2),
      composition: totalComposition > 0 ? (totalComposition % 1 === 0 ? String(totalComposition) : (Math.abs(totalComposition - 100) < 0.0001 ? "100" : String(Number(totalComposition.toFixed(4))))) : "0",
      solidLiquid: totalSolidLiquid > 0 ? (totalSolidLiquid % 1 === 0 ? String(totalSolidLiquid) : String(Number(totalSolidLiquid.toFixed(4)))) : "-",
      perSachet: totalPerSachetStr,
      finalDosing: totalFinalDosingStr,
      bffCost: totalBFFCost.toFixed(2),
      clientCost: totalClientCost.toFixed(2),
    },
    batchSummary: {
      output: {
        yield: outputYield,
        servingSize: outputServingSize > 0 ? outputServingSize : servingSizeValue,
        outputPieces: outputPieces > 0 ? (outputPieces % 1 === 0 ? String(outputPieces) : outputPieces.toFixed(2)) : "100",
        output: outputGrams > 0 ? (outputGrams % 1 === 0 ? String(outputGrams) : outputGrams.toFixed(2)) : "100",
      },
      costCalculation: {
        doughCostPerKg: {
          bff: (totalQuantity > 0 ? (totalBFFCost / totalQuantity) * 1000 : 0).toFixed(2),
          client: clientCostPerKgWithoutLoss.toFixed(2),
        },
        costPerKgWithLoss: {
          bff: (outputGrams > 0 ? (totalBFFCost / outputGrams) * 1000 : 0).toFixed(2),
          client: clientCostPerKgWithLoss.toFixed(2),
        },
        costPerPiece: {
          bff: costPerPieceBFF.toFixed(2),
          client: clientCostPerPiece.toFixed(2),
        },
      },
      bffCostCalculation: {
        totalBFFCost: totalBFFCost,
        batchSizeCost: totalBFFCost > 0 ? formatCostNum(totalBFFCost, "0") : "0",
        perPiece: formatCostNum(perPieceNum, "0"),
        costPerPiece: formatCostNum(costPerPieceBFF, "0"),
        packetQuantity: formatCostNum(packetQtyNum, "0"),
        labOutput: formatCostNum(labOutputGrams, "0"),
        labOutputCost: formatCostNum(labOutputCostBFF, "0"),
        labOutputCostUnit: "৳",
        wastage: formatCostNum(wastageGrams, "0"),
        wastageCost: formatCostNum(wastageCostBFF, "0"),
      },
      clientCostCalculation: {
        totalClientCost: totalClientCost,
        costPerKgWithoutLoss: clientCostPerKgWithoutLoss > 0 ? formatCostCurrency(clientCostPerKgWithoutLoss, "0") : "0",
        costPerKgWithLoss: clientCostPerKgWithLoss > 0 ? formatCostCurrency(clientCostPerKgWithLoss, "0") : "0",
        costPerPiece: clientCostPerPiece > 0 ? formatCostCurrency(clientCostPerPiece, "0") : "0",
      },
      confectionery: {
        batchSizeCost: totalBFFCost > 0 ? formatCostNum(totalBFFCost, "0") : "0",
        perPiece: formatCostNum(perPieceNum, "0"),
        packetQuantity: formatCostNum(packetQtyNum, "0"),
        labOutput: formatCostNum(labOutputGrams, "0"),
        wastage: formatCostNum(wastageGrams, "0"),
        costPerPiece: formatCostNum(costPerPieceBFF, "0"),
        costPerPacket: formatCostNum(costPerPieceBFF * packetQtyNum, "0"),
        labOutputCost: formatCostNum(labOutputCostBFF, "0"),
        wastageCost: formatCostNum(wastageCostBFF, "0"),
        clientCostPerKgWithoutLoss: clientCostPerKgWithoutLoss > 0 ? formatCostCurrency(clientCostPerKgWithoutLoss, "0") : "0",
        clientCostPerKgWithLoss: clientCostPerKgWithLoss > 0 ? formatCostCurrency(clientCostPerKgWithLoss, "0") : "0",
        clientCostPerPiece: clientCostPerPiece > 0 ? formatCostCurrency(clientCostPerPiece, "0") : "0",
      },
      solidLiquid: {
        solid: solidPercent.toFixed(2),
        liquid: liquidPercent.toFixed(2),
      },
    },
  };
}
